import React, { useMemo, useState } from 'react';
import { Download } from 'lucide-react';
import Button from '../shared/Button';
import { writeWorkbook, downloadBlob } from '../../utils/xlsx';

// The results of a v5 ↔ Opera Cloud comparison, shared by every Toolkit comparison: summary cards, a status filter,
// search, and an Excel export of what is shown.
//
// Props:
//   summary     — [{ label, value, tone? ('ok' | 'fail' | 'warn') }]
//   rows        — the comparison rows (each with `status` and `statusText`)
//   columns     — [{ key, label, render?(row) }]
//   statuses    — [{ value, label }] for the filter, in display order
//   exportName  — file name stem
//   note        — optional line under the cards (e.g. "matched rows are counted, not listed")

const TONE = { ok: 'otx-ok', fail: 'otx-fail', warn: 'tkb-warn-text' };

function ToolkitCompareResults({ summary, rows, columns, statuses, exportName, note }) {
    const [status, setStatus] = useState('all');
    const [search, setSearch] = useState('');
    const shown = useMemo(() => {
        const t = search.trim().toLowerCase();
        return rows.filter((r) => (status === 'all' || r.status === status)
            && (!t || columns.some((c) => String(r[c.key] ?? '').toLowerCase().includes(t))));
    }, [rows, status, search, columns]);
    const counts = useMemo(() => rows.reduce((m, r) => { m[r.status] = (m[r.status] || 0) + 1; return m; }, {}), [rows]);

    const exportXlsx = async () => {
        downloadBlob(await writeWorkbook([{ name: exportName, columns: ['Result', ...columns.map((c) => c.label)],
            rows: shown.map((r) => [r.statusText || r.status, ...columns.map((c) => r[c.key] ?? '')]) }]),
        `${exportName}-${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    return (
        <>
            <div className="otx-stats">
                {summary.map((s) => (
                    <div key={s.label} className="otx-stat">
                        <span className="otx-stat-label">{s.label}</span>
                        <span className={`otx-stat-value ${TONE[s.tone] || ''}`}>{s.value}</span>
                    </div>
                ))}
            </div>
            {note && <p className="tkb-hint">{note}</p>}
            <div className="tkb-toolbar">
                <input className="tkb-search" type="search" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
                <select className="tkb-select" value={status} onChange={(e) => setStatus(e.target.value)}>
                    <option value="all">All results ({rows.length})</option>
                    {statuses.filter((s) => counts[s.value]).map((s) => <option key={s.value} value={s.value}>{s.label} ({counts[s.value]})</option>)}
                </select>
                <div className="tkb-toolbar-spacer" />
                <Button variant="secondary" onClick={exportXlsx} disabled={!shown.length}><Download className="icon" /> Export</Button>
            </div>
            <div className="otx-table-wrap">
                <table className="otx-table">
                    <thead><tr><th>Result</th>{columns.map((c) => <th key={c.key}>{c.label}</th>)}</tr></thead>
                    <tbody>
                        {shown.map((r, i) => (
                            <tr key={i}>
                                <td><span className={`tkb-badge tkb-cmp-${r.status}`}>{r.statusText || r.status}</span></td>
                                {columns.map((c) => <td key={c.key}>{c.render ? c.render(r) : (r[c.key] ?? '')}</td>)}
                            </tr>
                        ))}
                        {!shown.length && <tr><td colSpan={columns.length + 1} className="otx-empty">Nothing to show.</td></tr>}
                    </tbody>
                </table>
            </div>
        </>
    );
}

export default ToolkitCompareResults;
